import {
  Avatar,
  AvatarFallback,
} from "@arcadeai/design-system/components/ui/atoms/avatar";
import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Card } from "@arcadeai/design-system/components/ui/atoms/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@arcadeai/design-system/components/ui/atoms/popover";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Globe, Loader2, Users, X } from "lucide-react";
import { useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { formatShortDate, getInitials } from "@/lib/utils";
import type { GroupedInvitation } from "../../types";
import { AcceptButton } from "./accept-button";

type InvitationCardProps = {
  invitation: GroupedInvitation;
  onAccept: (invitationId: string) => void;
  onAcceptAndSwitch: (projectId: string) => void;
  onDecline: (invitationId: string) => void;
  isAccepting?: boolean;
  isDeclining?: boolean;
  className?: string;
};
export function InvitationCard({
  invitation,
  onAccept,
  onAcceptAndSwitch,
  onDecline,
  isAccepting,
  isDeclining,
  className,
}: InvitationCardProps) {
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const [isAcceptOpen, setIsAcceptOpen] = useState(false);
  const [acceptPreference, setAcceptPreference] = useLocalStorage<
    "accept" | "acceptAndSwitch"
  >("invitation-accept-preference", "acceptAndSwitch");
  const primaryInvitationId = invitation.primary_invitation_id;
  const canInteract = Boolean(primaryInvitationId);

  const handleDecline = () => {
    if (!primaryInvitationId) {
      return;
    }
    onDecline(primaryInvitationId);
    setIsDeclineOpen(false);
  };

  const handleAccept = () => {
    if (!primaryInvitationId) {
      return;
    }
    setAcceptPreference("accept");
    onAccept(primaryInvitationId);
    setIsAcceptOpen(false);
  };

  const handleAcceptAndSwitch = () => {
    if (!primaryInvitationId) {
      return;
    }
    setAcceptPreference("acceptAndSwitch");
    onAcceptAndSwitch(invitation.project_id);
    setIsAcceptOpen(false);
  };

  const handleDefaultAction = () => {
    if (acceptPreference === "acceptAndSwitch") {
      handleAcceptAndSwitch();
    } else {
      handleAccept();
    }
  };

  return (
    <Card
      className={cn(
        "overflow-hidden border-muted/30 bg-muted/30 px-1 py-1 dark:bg-muted/10",
        className
      )}
    >
      <div className="space-y-4 p-4">
        {/* Header Section */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h3 className="font-semibold text-base leading-none">
              {invitation.project_name}
            </h3>
            <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
              <Globe className="h-3.5 w-3.5" />
              <span>{invitation.org_name}</span>
            </div>
          </div>
          <Badge className="shrink-0 font-normal text-xs" variant="secondary">
            Expires {formatShortDate(invitation.expires_at)}
          </Badge>
        </div>

        {/* Inviters List */}
        <div className="space-y-3 rounded-lg bg-background/50 p-3">
          <div className="flex items-center gap-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
            <Users className="h-3 w-3" />
            Invited by
          </div>
          <div className="space-y-3">
            {invitation.inviters.map((inviter) => (
              <div
                className="flex items-center gap-3"
                key={inviter.invitation_id}
              >
                <Avatar className="h-8 w-8 border">
                  <AvatarFallback className="text-[10px]">
                    {getInitials(inviter.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm leading-none">
                    {inviter.name}
                  </div>
                  <div className="mt-0.5 truncate text-muted-foreground text-xs">
                    {inviter.email}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3 pt-1">
          <Popover onOpenChange={setIsDeclineOpen} open={isDeclineOpen}>
            <PopoverTrigger
              render={
                <Button
                  className="w-full hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                  disabled={!canInteract || isDeclining}
                  variant="outline"
                />
              }
            >
              {isDeclining ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <X className="mr-2 h-4 w-4" />
              )}
              <span>{isDeclining ? "Declining..." : "Decline"}</span>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">
                    Decline Invitation
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    Are you sure you want to decline the invitation to join{" "}
                    <span className="font-medium">
                      {invitation.project_name}
                    </span>
                    ? This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => setIsDeclineOpen(false)}
                    size="sm"
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={isDeclining}
                    onClick={handleDecline}
                    size="sm"
                    variant="destructive"
                  >
                    {isDeclining ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Decline
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <AcceptButton
            acceptPreference={acceptPreference}
            disabled={!canInteract || Boolean(isAccepting)}
            isAccepting={Boolean(isAccepting)}
            isDropdownOpen={isAcceptOpen}
            onAccept={handleAccept}
            onAcceptAndSwitch={handleAcceptAndSwitch}
            onDefaultAction={handleDefaultAction}
            onDropdownOpenChange={setIsAcceptOpen}
            projectName={invitation.project_name}
          />
        </div>
      </div>
    </Card>
  );
}
